/**
 * Portfolio & Profile Hooks
 *
 * React Query hooks for portfolio and profile management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { portfolioApi, profileApi } from '../api/easelApi'
import { useToast } from '@/hooks/use-toast'
import type { PaletteSuggestionRequest, SaveAppearanceRequest, SocialAccounts, StyleConfig } from '../types'

// =============================================================================
// Query Keys
// =============================================================================

export const portfolioKeys = {
  all: ['portfolio'] as const,
  status: () => [...portfolioKeys.all, 'status'] as const,
  profile: () => [...portfolioKeys.all, 'profile'] as const,
  links: () => [...portfolioKeys.all, 'links'] as const,
  social: () => [...portfolioKeys.all, 'social'] as const,
  style: () => [...portfolioKeys.all, 'style'] as const,
  preview: () => [...portfolioKeys.all, 'preview'] as const,
}

// =============================================================================
// Portfolio Hooks
// =============================================================================

export function usePortfolioStatus() {
  return useQuery({
    queryKey: portfolioKeys.status(),
    queryFn: portfolioApi.getStatus,
  })
}

export function useInitializePortfolio() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: portfolioApi.initialize,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portfolioKeys.all })
      toast({
        title: 'Portfolio Initialized',
        description: 'Your portfolio has been created successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to initialize portfolio',
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// Profile Hooks
// =============================================================================

export function useProfile() {
  return useQuery({
    queryKey: portfolioKeys.profile(),
    queryFn: profileApi.getProfile,
  })
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: profileApi.updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portfolioKeys.profile() })
      toast({
        title: 'Profile Updated',
        description: 'Your profile has been updated successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update profile',
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// Links Hooks
// =============================================================================

export function useProfileLinks() {
  return useQuery({
    queryKey: portfolioKeys.links(),
    queryFn: profileApi.getLinks,
  })
}

export function useCreateLink() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: profileApi.createLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portfolioKeys.links() })
      toast({
        title: 'Link Created',
        description: 'Your link has been added successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create link',
        variant: 'destructive',
      })
    },
  })
}

export function useUpdateLink() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof profileApi.updateLink>[1] }) =>
      profileApi.updateLink(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portfolioKeys.links() })
      toast({
        title: 'Link Updated',
        description: 'Your link has been updated successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update link',
        variant: 'destructive',
      })
    },
  })
}

export function useDeleteLink() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: profileApi.deleteLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portfolioKeys.links() })
      toast({
        title: 'Link Deleted',
        description: 'Your link has been removed.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete link',
        variant: 'destructive',
      })
    },
  })
}

export function useReorderLinks() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: profileApi.reorderLinks,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portfolioKeys.links() })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to reorder links',
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// Social Hooks
// =============================================================================

export function useSocialAccounts() {
  return useQuery({
    queryKey: portfolioKeys.social(),
    queryFn: profileApi.getSocial,
  })
}

export function useUpdateSocial() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: Partial<SocialAccounts>) => profileApi.updateSocial(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portfolioKeys.social() })
      toast({
        title: 'Social Accounts Updated',
        description: 'Your social accounts have been updated.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update social accounts',
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// Style Hooks
// =============================================================================

export function useStyleConfig() {
  return useQuery({
    queryKey: portfolioKeys.style(),
    queryFn: profileApi.getStyle,
  })
}

export function useUpdateStyle() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: Partial<StyleConfig>) => profileApi.updateStyle(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portfolioKeys.style() })
      toast({
        title: 'Appearance saved',
        description: 'Your page will show the new look within a minute.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update style',
        variant: 'destructive',
      })
    },
  })
}

/** The artist's own palette (from their genome) to recommend on the Appearance page. */
export function useStyleRecommendations() {
  return useQuery({
    queryKey: [...portfolioKeys.style(), 'recommendations'],
    queryFn: profileApi.getStyleRecommendations,
    staleTime: 5 * 60 * 1000,
  })
}

/** The artist's surfaces and their colours, for the Appearance page. */
export function useAppearance() {
  return useQuery({
    queryKey: [...portfolioKeys.style(), 'appearance'],
    queryFn: profileApi.getAppearance,
  })
}

/** Save a palette to one or both surfaces. */
export function useSaveAppearance() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: SaveAppearanceRequest) => profileApi.saveAppearance(data),
    onSuccess: (result) => {
      queryClient.setQueryData([...portfolioKeys.style(), 'appearance'], {
        artwork: result.artwork,
        surfaces: result.surfaces,
      })
      queryClient.invalidateQueries({ queryKey: portfolioKeys.style() })
      const where = result.saved.length > 1 ? 'your link-in-bio and website' : result.saved[0] === 'website' ? 'your website' : 'your link-in-bio'
      toast({
        title: 'Appearance saved',
        description: `New colours are live on ${where}. Websites can take up to five minutes to update.`,
      })
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      toast({
        title: 'Could not save',
        description: error.response?.data?.message || error.message || 'Please try again.',
        variant: 'destructive',
      })
    },
  })
}

/** Palette ideas from a description and/or an image. Errors are shown in the conversation, not a toast. */
export function useSuggestPalettes() {
  return useMutation({
    mutationFn: (input: PaletteSuggestionRequest) => profileApi.suggestPalettes(input),
  })
}

// =============================================================================
// Preview Hooks
// =============================================================================

export function usePreviewUrl() {
  return useQuery({
    queryKey: portfolioKeys.preview(),
    queryFn: profileApi.getPreviewUrl,
  })
}
